// src/pages/Privacy.jsx
import React from "react";
import Container from "../components/Container";

export default function Privacy() {
  return (
    <main className="bg-white">
      <Container className="max-w-3xl py-12 sm:py-16">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]">Privacy Notice</h1>
          <p className="mt-2 text-sm text-slate-600">Last updated: 1 October 2025</p>
          <p className="mt-4 text-[#003366]">
            This Privacy Notice explains how <strong>Ethira Property Group</strong> (“Ethira”, “we”, “us”)
            collects, uses, shares and protects your personal information when you interact with our website,
            calculators and downloadable guides, or when you contact us. It is designed to be clear and concise
            and to meet our obligations under UK data protection law (UK GDPR and the Data Protection Act 2018).
          </p>
          <p className="mt-2 text-[#003366]">
            <em><strong>In short:</strong> we collect the minimum information needed to respond to your requests,
            provide downloads and calculators, improve our services, and—if you choose—send relevant updates.
            We don’t sell your data, and you can opt out of marketing at any time.</em>
          </p>
          <nav className="mt-4 text-sm">
            <a href="#cookie-policy" className="underline text-[#003366]">Skip to Cookie Policy</a>
            <span className="mx-2 text-slate-300">|</span>
            <button
              type="button"
              onClick={() => window.showCookieSettings?.()}
              className="underline text-[#003366]"
            >
              Cookie Settings
            </button>
          </nav>
        </header>

        <section className="prose prose-slate max-w-none">
          <h2 id="who-we-are">1) Who we are</h2>
          <p><strong>Controller:</strong> Ethira Property Group</p>
          <p><strong>Contact:</strong> <a href="mailto:info@ethirapropertygroup.co.uk">info@ethirapropertygroup.co.uk</a></p>
          <p><strong>Postal:</strong> 2 Frederick Street, Kings Cross, London, United Kingdom, WC1X 0ND</p>

          <h2 id="data-we-collect">2) The data we collect</h2>
          <ul>
            <li><strong>Identity & contact:</strong> name, email, phone (country code + local number), and anything you write in message fields.</li>
            <li><strong>Engagement:</strong> guides downloaded, calculators completed, and your selections (e.g., “Ready to invest?”, “Amount to invest?”).</li>
            <li><strong>Technical:</strong> IP address, device/browser info, pages viewed, referral source, and cookie/analytics data.</li>
            <li><strong>Communications:</strong> our correspondence with you.</li>
          </ul>
          <p>We do not deliberately collect special category data or data about children.</p>

          <h2 id="how-we-collect">3) How we collect your data</h2>
          <ul>
            <li><strong>Directly from you</strong> via enquiry/download forms and calculators.</li>
            <li><strong>Automatically</strong> via cookies and similar technologies (see <a href="#cookie-policy">Cookie Policy</a>).</li>
          </ul>

          <h2 id="why-we-use">4) Why we use your data (purposes & lawful bases)</h2>
          <div className="overflow-x-auto not-prose">
            <table className="min-w-full border border-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left border-b">Purpose</th>
                  <th className="p-2 text-left border-b">Examples</th>
                  <th className="p-2 text-left border-b">Lawful basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 align-top border-b">Provide what you ask for</td>
                  <td className="p-2 align-top border-b">Respond to enquiries; send the guide you requested; book a consultation</td>
                  <td className="p-2 align-top border-b"><strong>Contract</strong> (steps at your request) and <strong>Legitimate interests</strong></td>
                </tr>
                <tr>
                  <td className="p-2 align-top border-b">Tailor our advice</td>
                  <td className="p-2 align-top border-b">Record calculator answers and guide selections to prepare for a call</td>
                  <td className="p-2 align-top border-b"><strong>Legitimate interests</strong></td>
                </tr>
                <tr>
                  <td className="p-2 align-top border-b">Marketing you opt into</td>
                  <td className="p-2 align-top border-b">News and updates about property investing and Ethira services</td>
                  <td className="p-2 align-top border-b"><strong>Consent</strong> (withdraw any time)</td>
                </tr>
                <tr>
                  <td className="p-2 align-top border-b">Site operation & security</td>
                  <td className="p-2 align-top border-b">Content delivery, security, error logs, performance analytics</td>
                  <td className="p-2 align-top border-b"><strong>Legitimate interests</strong></td>
                </tr>
                <tr>
                  <td className="p-2 align-top">Legal/compliance & record-keeping</td>
                  <td className="p-2 align-top">Handling complaints; demonstrating consent; tax and audit</td>
                  <td className="p-2 align-top"><strong>Legal obligation</strong> / <strong>Legitimate interests</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 id="marketing">5) Marketing communications</h2>
          <p>
            If you tick the consent box on a form, we may send you emails with relevant updates (for example, new guides
            or important service updates). You can opt out at any time using the unsubscribe link in our emails or by
            emailing <a href="mailto:info@ethirapropertygroup.co.uk">info@ethirapropertygroup.co.uk</a>. We will still
            contact you about services you have requested or transactions you initiate.
          </p>

          <h2 id="sharing">6) Sharing your data</h2>
          <p>
            We may share your personal data with carefully selected service providers who help us run our website and
            deliver our services (for example, website hosting, analytics, communications, and secure form handling).
            These providers act on our instructions and are required to keep your data secure and confidential. We do
            <strong> not</strong> sell your personal data.
          </p>

          <h2 id="transfers">7) International transfers</h2>
          <p>
            Where data is processed outside the UK, we use appropriate safeguards (such as the UK International Data
            Transfer Agreement or Standard Contractual Clauses) and additional measures where required.
          </p>

          <h2 id="retention">8) How long we keep your data</h2>
          <ul>
            <li><strong>Enquiries/consultation records:</strong> up to 24 months after last meaningful interaction.</li>
            <li><strong>Guide downloads & calculators:</strong> up to 24 months.</li>
            <li><strong>Marketing contacts:</strong> until you unsubscribe; we keep a minimal suppression record.</li>
            <li><strong>Analytics:</strong> typically up to 26 months (subject to configuration).</li>
            <li><strong>Legal/operational records:</strong> as required by law or to establish/defend claims.</li>
          </ul>

          <h2 id="rights">9) Your rights</h2>
          <p>
            You can request access, rectification, erasure, restriction, objection (including profiling), withdraw consent,
            and data portability. To exercise your rights, email{" "}
            <a href="mailto:info@ethirapropertygroup.co.uk">info@ethirapropertygroup.co.uk</a>. We may need to verify your
            identity. If you’re not satisfied, you can complain to the UK ICO (ico.org.uk / 0303 123 1113).
          </p>

          <h2 id="security">10) Security</h2>
          <p>
            We use appropriate technical and organisational measures (encryption in transit, access controls,
            least-privilege permissions, and regular reviews). No system is 100% secure; please take care when
            transmitting data online.
          </p>

          <h2 id="children">11) Children</h2>
          <p>Our services aren’t directed at children and we don’t knowingly collect children’s data.</p>

          <h2 id="automated">12) Automated decision-making</h2>
          <p>We don’t make decisions with legal or similarly significant effects based solely on automated processing.</p>

          <h2 id="links">13) Third-party links</h2>
          <p>Third-party sites we link to have their own privacy statements.</p>

          <h2 id="changes">14) Changes to this notice</h2>
          <p>We may update this notice. Material changes will appear here with a new “Last updated” date.</p>

          <h2 id="contact">15) Contact us</h2>
          <p>
            <strong>Email:</strong> <a href="mailto:info@ethirapropertygroup.co.uk">info@ethirapropertygroup.co.uk</a><br />
            <strong>Post:</strong> 2 Frederick Street, Kings Cross, London, United Kingdom, WC1X 0ND
          </p>

          <hr className="my-10" />

          <h2 id="cookie-policy">Cookie Policy</h2>
          <p className="text-sm text-slate-600">Last updated: 1 October 2025</p>

          <h3>What are cookies?</h3>
          <p>
            Cookies are small text files placed on your device by websites you visit. They can be essential for the site
            to function, used to measure performance, or—if you consent—used for marketing.
          </p>

          <h3>How we use cookies</h3>
          <h4>Essential</h4>
          <ul>
            <li>Security and load balancing (to keep the site reliable and protect against abuse).</li>
            <li>Remembering your cookie preferences and basic site settings.</li>
          </ul>

          <h4>Analytics (with your consent)</h4>
          <ul>
            <li>Understanding page views, feature usage and improving the site experience.</li>
          </ul>

          <h4>Advertising (with your consent)</h4>
          <ul>
            <li>Measuring conversions (e.g., successful enquiries or downloads) and managing remarketing.</li>
          </ul>

          <h3>Managing your choices</h3>
          <p>
            On your first visit we present a banner to set your preferences. You can change or withdraw consent at any
            time via{" "}
            <button
              type="button"
              onClick={() => window.showCookieSettings?.()}
              className="ml-1 underline text-[#003366]"
            >
              Cookie Settings
            </button>
            . You can also control cookies via your browser; blocking some cookies may impact functionality.
          </p>

          <h3>Updates</h3>
          <p>We may update this policy to reflect changes to cookies or legal requirements.</p>

          <h3>Contact</h3>
          <p>
            Questions about cookies? Email{" "}
            <a href="mailto:info@ethirapropertygroup.co.uk">info@ethirapropertygroup.co.uk</a>.
          </p>

          <hr className="my-10" />
          <p className="text-xs text-slate-500">
            This page is provided for transparency and does not constitute legal advice.
          </p>
        </section>
      </Container>
    </main>
  );
}
